import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import Breadcrumb from '../components/Breadcrumb'
import KPICard from '../components/KPICard'
import { exportMultiSheet } from '../utils/exportExcel'
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts'

const CATEGORY_COLORS = [
    '#f97316', '#22c55e', '#3b82f6', '#a855f7', '#ef4444',
    '#06b6d4', '#eab308', '#ec4899', '#64748b', '#14b8a6',
    '#f43f5e', '#8b5cf6', '#d946ef', '#0ea5e9', '#84cc16',
    '#fb923c', '#38bdf8', '#a3e635', '#c084fc', '#fbbf24'
]

function formatDollars(val) {
    if (val == null) return '$0'
    const abs = Math.abs(val)
    if (abs >= 1_000_000) return `${val < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(1)}M`
    if (abs >= 1_000) return `${val < 0 ? '-' : ''}$${(abs / 1_000).toFixed(0)}K`
    return `$${val.toLocaleString()}`
}

function LOEDetail() {
    const { theme } = useTheme()
    const { category } = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const yearFromUrl = searchParams.get('year')
    const [selectedYear, setSelectedYear] = useState(yearFromUrl ? parseInt(yearFromUrl) : null)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        fetchData()
    }, [category, selectedYear])

    const fetchData = async () => {
        setLoading(true)
        setError(null)
        try {
            const params = new URLSearchParams()
            if (category) params.set('category', category)
            if (selectedYear) params.set('year', selectedYear)
            const qs = params.toString()
            const url = `${apiConfig.baseUrl}/financials/loe${qs ? '?' + qs : ''}`
            const res = await fetch(url)
            if (!res.ok) throw new Error('Failed to fetch LOE data')
            setData(await res.json())
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const handleYearChange = (yr) => {
        setSelectedYear(yr === selectedYear ? null : yr)
    }

    // Navigate to month detail page
    const navigateToMonth = (row) => {
        if ((row.actual || 0) <= 0) return
        const yr = row.year || Math.floor((row.ym || 0) / 100)
        const mo = row.monthNum || ((row.ym || 0) % 100)
        if (!yr || !mo) return
        const pad = String(mo).padStart(2, '0')
        navigate(`/financials/loe/month/${yr}-${pad}`)
    }

    // Handle chart click — find the month row and navigate
    const handleChartClick = (chartData) => {
        if (!chartData?.activePayload?.length) return
        const clickedName = chartData.activeLabel
        const row = (data?.monthly || []).find(m =>
            (m.shortLabel || m.month) === clickedName
        )
        if (row) navigateToMonth(row)
    }

    const displayYear = data?.periodLabel || data?.year || selectedYear || new Date().getFullYear()
    const isDark = theme === 'dark'
    const yearParam = selectedYear ? `?year=${selectedYear}` : ''
    const allCategories = data?.allCategories || []
    const availableYears = data?.availableYears || []

    const handleCategoryChange = (e) => {
        const val = e.target.value
        if (val === '') {
            navigate(`/financials/loe${yearParam}`)
        } else {
            navigate(`/financials/loe/${encodeURIComponent(val)}${yearParam}`)
        }
    }

    const handleExport = () => {
        if (!data) return
        const periodLabel = data.isRolling ? 'Rolling 13 Mo' : String(displayYear)
        const rows = []

        // Unpivot: one row per month per category
        if ((data.monthlyByCategory || []).length > 0 && categoryNames.length > 0) {
            for (const m of data.monthlyByCategory) {
                const month = m.shortLabel || m.month
                for (const cat of categoryNames) {
                    const val = Math.round(m[cat] || 0)
                    if (val > 0) rows.push([month, cat, val])
                }
            }
        }

        // Monthly totals sheet
        const totalRows = (data.monthly || [])
            .filter(m => (m.actual || 0) > 0)
            .map(m => [m.shortLabel || m.month, Math.round(m.actual)])

        const catSuffix = category ? `_${decodeURIComponent(category).replace(/[^a-zA-Z0-9]/g, '_')}` : ''
        exportMultiSheet([
            { name: 'LOE Data', data: [['Date', 'LOE Category', 'Amount'], ...rows] },
            { name: 'Monthly Totals', data: [['Date', 'Total LOE'], ...totalRows] }
        ], `LOE_Detail_${periodLabel}${catSuffix}`)
    }

    const ExportBtn = ({ onClick, label = 'Export' }) => (
        <button onClick={onClick}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all flex items-center gap-1 ${
                isDark
                    ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white'
                    : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
            }`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {label}
        </button>
    )

    const breadcrumbs = [
        { label: 'Financials', path: '/financials' },
        { label: 'LOE', path: category ? `/financials/loe${selectedYear ? '?year=' + selectedYear : ''}` : null },
    ]
    if (category) {
        breadcrumbs.push({ label: decodeURIComponent(category) })
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    const chartData = (data?.monthly || []).map(m => ({ ...m, name: m.shortLabel || m.month }))
    const chartDataWithActuals = chartData.filter(m => m.actual > 0)

    const stackedData = (data?.monthlyByCategory || []).map(m => ({
        ...m, name: m.shortLabel || m.month
    })).filter(m => {
        const cats = data?.categoryNames || []
        return cats.some(c => (m[c] || 0) > 0)
    })

    const categoryNames = data?.categoryNames || []

    const chartColors = {
        actual: '#f97316',
        grid: isDark ? '#334155' : '#e2e8f0',
        text: isDark ? '#94a3b8' : '#64748b',
        tooltipBg: isDark ? '#1e293b' : '#ffffff',
        tooltipBorder: isDark ? '#334155' : '#e2e8f0'
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null
        return (
            <div style={{
                background: chartColors.tooltipBg,
                border: `1px solid ${chartColors.tooltipBorder}`,
                borderRadius: 8, padding: '10px 14px', fontSize: 13,
                maxHeight: 300, overflowY: 'auto'
            }}>
                <p style={{ fontWeight: 600, marginBottom: 4, color: isDark ? '#fff' : '#111' }}>{label}</p>
                {payload.filter(e => e.value > 0).map((entry, i) => (
                    <p key={i} style={{ color: entry.color, margin: '2px 0' }}>
                        {entry.name}: ${Math.round(entry.value).toLocaleString()}
                    </p>
                ))}
            </div>
        )
    }

    const loeByCategory = data?.loeByCategory || []

    return (
        <div className="space-y-6">
            <Breadcrumb items={breadcrumbs} />

            <div className={`border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                            <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                            {category ? `LOE: ${decodeURIComponent(category)}` : 'LOE Detail'}
                        </h1>
                        <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                            {data?.isRolling ? `Rolling 13 months` : displayYear} {'\u2022'} Data through {data?.periodLabel || displayYear}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 ml-3 sm:ml-0">
                        {/* Year Toggle */}
                        <div className="flex gap-1">
                            <button onClick={() => setSelectedYear(null)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                                    selectedYear === null
                                        ? 'bg-blue-600 text-white shadow-lg'
                                        : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}>13 Mo</button>
                            {availableYears.map(yr => (
                                <button key={yr} onClick={() => handleYearChange(yr)}
                                    className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                                        yr === selectedYear
                                            ? 'bg-blue-600 text-white shadow-lg'
                                            : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}>{yr}</button>
                            ))}
                        </div>
                        {/* Category Dropdown */}
                        {allCategories.length > 0 && (
                            <select
                                value={category ? decodeURIComponent(category) : ''}
                                onChange={handleCategoryChange}
                                className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                                    isDark
                                        ? 'bg-slate-800 border-slate-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-xs`}
                            >
                                <option value="">All Categories</option>
                                {allCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                    <p>{error}</p>
                </div>
            )}

            {data && (
                <>
                    {/* Summary KPIs */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <KPICard title={`${displayYear} Actual`} value={`$${Math.round(data.ytdActual || 0).toLocaleString()}`} />
                        <KPICard title="Monthly Avg"
                            value={`$${Math.round((data.ytdActual || 0) / Math.max(chartDataWithActuals.length, 1)).toLocaleString()}`}
                            subtitle={`${chartDataWithActuals.length} months`} />
                    </div>

                    {/* Monthly LOE Trend Line Chart */}
                    {chartDataWithActuals.length > 0 && (
                        <div className="chart-container">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Monthly LOE Trend
                                    <span className={`text-xs font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Click a month to drill down
                                    </span>
                                </h2>
                                <ExportBtn onClick={handleExport} />
                            </div>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={chartDataWithActuals} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                                    onClick={handleChartClick} style={{ cursor: 'pointer' }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis dataKey="name" tick={{ fill: chartColors.text, fontSize: 12 }} />
                                    <YAxis tickFormatter={formatDollars} tick={{ fill: chartColors.text, fontSize: 12 }} width={65} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: 13, color: chartColors.text }} />
                                    <Line type="monotone" dataKey="actual" name="LOE Actual"
                                        stroke={chartColors.actual} strokeWidth={2} dot={{ r: 4, cursor: 'pointer' }}
                                        activeDot={{ r: 6, cursor: 'pointer' }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* LOE by Category - Stacked Bar (only on overview) */}
                    {!category && stackedData.length > 0 && categoryNames.length > 0 && (
                        <div className="chart-container">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    LOE by Category
                                    <span className={`text-xs font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Click a month to drill down
                                    </span>
                                </h2>
                                <ExportBtn onClick={handleExport} />
                            </div>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={stackedData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                                    onClick={handleChartClick} style={{ cursor: 'pointer' }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis dataKey="name" tick={{ fill: chartColors.text, fontSize: 12 }} />
                                    <YAxis tickFormatter={formatDollars} tick={{ fill: chartColors.text, fontSize: 12 }} width={65} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: 12, color: chartColors.text }} />
                                    {categoryNames.map((cat, i) => (
                                        <Bar key={cat} dataKey={cat} name={cat} stackId="loe"
                                            fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]}
                                            radius={i === categoryNames.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                                            cursor="pointer" />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* LOE Category Breakdown Table (only on overview) */}
                    {!category && loeByCategory.length > 0 && (
                        <div className="chart-container">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    LOE Breakdown ({displayYear})
                                </h2>
                                <ExportBtn onClick={handleExport} label="Export All" />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th className="text-right">Actual</th>
                                            <th className="text-right">% of Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loeByCategory.map((row, i) => {
                                            const pctOfTotal = data.ytdActual ? ((row.actual / data.ytdActual) * 100).toFixed(1) : '--'
                                            return (
                                                <tr key={i} className="cursor-pointer hover:bg-slate-700/30"
                                                    onClick={() => navigate(`/financials/loe/${encodeURIComponent(row.category)}${yearParam ? yearParam : ''}`)}>
                                                    <td className="font-medium">{row.category}</td>
                                                    <td className="text-right">${Math.round(row.actual || 0).toLocaleString()}</td>
                                                    <td className="text-right">{pctOfTotal}%</td>
                                                </tr>
                                            )
                                        })}
                                        <tr className={`font-bold ${isDark ? 'border-t-2 border-slate-500' : 'border-t-2 border-gray-400'}`}>
                                            <td>Total LOE</td>
                                            <td className="text-right">${Math.round(data.ytdActual || 0).toLocaleString()}</td>
                                            <td className="text-right">100%</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Monthly Breakdown Table */}
                    {data.monthly && data.monthly.length > 0 && (
                        <div className="chart-container">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Monthly Breakdown
                                    <span className={`text-xs font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Click a month to drill down
                                    </span>
                                </h2>
                                <ExportBtn onClick={handleExport} />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Month</th>
                                            <th className="text-right">Actual</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.monthly.map((row, i) => {
                                            const hasActual = (row.actual || 0) > 0
                                            return (
                                                <tr key={i}
                                                    className={hasActual ? 'cursor-pointer hover:bg-slate-700/30' : ''}
                                                    onClick={() => hasActual && navigateToMonth(row)}>
                                                    <td className="font-medium">
                                                        {hasActual && <span className="inline-block mr-2 text-blue-400">&#8250;</span>}
                                                        {row.month}
                                                    </td>
                                                    <td className="text-right">{hasActual ? `$${Math.round(row.actual).toLocaleString()}` : '--'}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default LOEDetail
